import { useEffect, useState, type ReactNode } from 'react';
import { mediaLabel, useAudioTrack, useMedia, useVideoTrack, type VideoSource } from '../live/media';
import { useLive } from '../live/store';
import { tMaybe, useT } from '../i18n';

/** Seconds left until `at` (ms epoch), ticking once per second; null when not set/past. */
function useCountdown(at: number | undefined): number | null {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!at) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [at]);
  if (!at) return null;
  const left = Math.ceil((at - now) / 1000);
  return left > 0 ? left : null;
}

/**
 * Video area: LiveKit track when available, otherwise the design's scanline placeholder + state text
 * (spinner while waiting for the track, "Retrying in Ns" after start_failed).
 * `topExtra` is rendered next to the LIVE badge (e.g. the Camera/Screen switch).
 */
export function RobotVideo({
  serial,
  online,
  liveLabel,
  placeholder,
  source = 'camera',
  hideBadge,
  topExtra,
}: {
  serial: string;
  online: boolean;
  liveLabel?: string;
  placeholder?: string;
  source?: VideoSource;
  hideBadge?: boolean;
  topExtra?: ReactNode;
}) {
  const t = useT();
  const info = useMedia((s) => s.byRobot[serial]);
  const notConfigured = useMedia((s) => s.notConfigured);
  const screenStatus = useLive((s) => (s.robots[serial]?.status?.media as { screen?: unknown } | undefined)?.screen);
  const ref = useVideoTrack(serial, source);
  const retryIn = useCountdown(info?.retryAt);
  const label = mediaLabel(info, notConfigured, online, source, screenStatus);
  const stalled = Boolean(source === 'screen' ? info?.stalled?.screen : info?.stalled?.camera);
  const hasVideo = Boolean(source === 'screen' ? info?.hasScreen : info?.hasVideo) && !stalled;
  const waiting = online && !hasVideo && !notConfigured && info && info.state !== 'error' && info.state !== 'disconnected' && screenStatus !== 'needs_consent';
  const showBadge = online && !hideBadge && (hasVideo || stalled || info?.state === 'connected');
  return (
    <>
      {!hasVideo && <div className="scan" />}
      <video ref={ref} className="video-el" autoPlay playsInline muted data-source={source} style={{ visibility: hasVideo ? 'visible' : 'hidden' }} />
      {!hasVideo && (
        <span className="video-state">
          {waiting && <span className="spinner" aria-hidden />}
          {label ? tMaybe(label) : placeholder}
          {retryIn !== null && <span className="retry-in">{t('media.retryIn', { n: retryIn })}</span>}
        </span>
      )}
      {(showBadge || topExtra) && (
        <div className="live-top">
          {showBadge && (
            <span className="live-badge">
              <span className="rec" />
              {liveLabel ?? t('video.live')}
            </span>
          )}
          {topExtra}
        </div>
      )}
    </>
  );
}

export function RobotAudio({ serial, enabled, volume = 1, muted = false }: { serial: string; enabled: boolean; volume?: number; muted?: boolean }) {
  const ref = useAudioTrack(serial, enabled, volume, muted);
  return <audio ref={ref} autoPlay />;
}
